// 🧱🗂️ The room frame's PAGE side (SPACES_REDESIGN_BRAINSTORM_2026_09_29
// §5.2-§5.5a · §5.12; the approved mockup rooms-by-purpose.html, frame A).
// GameStepPage mounts components/RoomShell behind utils/roomPurposeLayout's
// gate (shellPresetFor: learn → Workstation, research → Research, the switch
// ON since 10-01); these are the small decisions it
// makes around that mount, kept pure so they are pinned in jest:
//   · the composer's labeled Share (COMPOSER_TOOLS.share): Doc · Image · Video
//     ride the EXISTING upload (HumanInputForm onUploadFile: the backend takes
//     image/, video/, application/pdf, text/, .txt .md .markdown .csv .docx up
//     to 7 MB), Screen is LivestreamPanel's own screen share (desktop, and only
//     where getDisplayMedia exists: never iOS)
//   · the ⋯ menu's page items (Export chat · Room details · Promote on phones)
//   · when the stage (the frame's slot above the feed) shows the live panel
//   · the check-in's "Something lighter" request (care_lighter on the
//     interjection route, the viewer's own seat proof; backend careLighter.js)
// No React, no DOM beyond the guarded navigator read.

// ── the composer's Share ────────────────────────────────────────────────────
export const SHARE_ACCEPT = Object.freeze({
  doc: 'application/pdf,text/*,.txt,.md,.markdown,.csv,.docx',
  image: 'image/*',
  video: 'video/*',
});

/** Can this browser share its screen? (getDisplayMedia: absent on iOS and most phones) */
export function canShareScreen(nav = (typeof navigator !== 'undefined' ? navigator : null)) {
  try {
    return !!(nav && nav.mediaDevices && typeof nav.mediaDevices.getDisplayMedia === 'function');
  } catch (e) {
    return false;
  }
}

/**
 * The Share menu, in the mockup's order: Doc · Image · Video, then Screen only
 * when the page can really start one (`canScreen`: a desktop page that handed
 * the composer a screen start AND a browser with getDisplayMedia).
 * → [{ key, label, hint, accept? }]
 */
export function shareMenuItems({ canScreen = false } = {}) {
  const items = [
    { key: 'doc', label: 'Doc', hint: 'PDF, docx, md', accept: SHARE_ACCEPT.doc },
    { key: 'image', label: 'Image', hint: '7 MB', accept: SHARE_ACCEPT.image },
    { key: 'video', label: 'Video', hint: '7 MB', accept: SHARE_ACCEPT.video },
  ];
  if (canScreen) items.push({ key: 'screen', label: 'Screen', hint: 'desktop' });
  return items;
}

/**
 * 📺 The Stream Eyes consent line (STREAM_EYES_SPEC_2026_08_05 §2.2, Nina's
 * call: "shown beside every go-live CTA, for camera AND screen"). The menu's
 * Screen is a go-live CTA, so it carries LivestreamPanel's own words.
 */
export const SHARE_SCREEN_NOTE = 'Heads up: the AIs in this space can see your stream';

// ── the check-in's "Mute room sounds for 10 min" ────────────────────────────
// The room's mute is stored (SoundManager + hostVoiceMuted live in
// localStorage), so a page left mid-break would keep every room silent for
// good. The deadline is stored too and read again on every mount: the sound
// comes back at the 10-minute mark on this page, or on the next one.
export const CARE_MUTE_KEY = 'care_mute_until_v1';

/** The pending mute's end (epoch ms), or 0 = none on this device. */
export function careMuteUntil(store) {
  try {
    const v = Number(store ? store.getItem(CARE_MUTE_KEY) : 0);
    return Number.isFinite(v) && v > 0 ? v : 0;
  } catch (e) {
    return 0;
  }
}
export function setCareMuteUntil(store, untilMs) {
  try { if (store && Number(untilMs) > 0) store.setItem(CARE_MUTE_KEY, String(Math.round(Number(untilMs)))); } catch (e) { /* in memory only: this page's timer still runs */ }
}
export function clearCareMute(store) {
  try { if (store) store.removeItem(CARE_MUTE_KEY); } catch (e) { /* nothing stored */ }
}

// ── the ⋯ menu ──────────────────────────────────────────────────────────────
/**
 * The page's items for the frame's ⋯ (the kit adds Music itself). Promote is
 * a top-bar door on desktop; on phones it lives here, for whoever may promote
 * the room (utils/roomChrome.canPromote: everyone in a public room, the mod in
 * a hidden one). A missing handler = no item.
 */
export function shellMenuItems({ isMobile = false, canPromote = false, onExport = null, onDetails = null, onPromote = null } = {}) {
  const items = [];
  if (typeof onExport === 'function') items.push({ key: 'export', label: 'Export chat', icon: 'download', onSelect: onExport });
  if (typeof onDetails === 'function') items.push({ key: 'details', label: 'Room details', icon: 'ⓘ', onSelect: onDetails });
  if (isMobile && canPromote && typeof onPromote === 'function') items.push({ key: 'promote', label: 'Promote', icon: '📣', onSelect: onPromote });
  return items;
}

// ── the stage ───────────────────────────────────────────────────────────────
/** Who is sharing a SCREEN right now, as one stable key ('' = nobody). */
export function screenShareKey(streamers) {
  return (Array.isArray(streamers) ? streamers : [])
    .filter((s) => s && s.mode === 'screen' && s.id != null)
    .map((s) => String(s.id))
    .sort()
    .join(',');
}

/**
 * Does the frame's stage hold the live panel?
 *   publishing  this viewer is live (never hidden from under them)
 *   asked       the viewer asked (Share → Screen, a go-live line, ▶ Watch)
 *   connected   the panel is in the LiveKit room (watching)
 *   screenKey   screenShareKey(): someone shares a screen = the join poster
 *               (a camera never takes the stage: the legacy stage's rule)
 *   closedFor   the screenKey the viewer closed the stage on (null = never);
 *               a NEW share opens it again
 */
export function shellStageLive({ publishing = false, asked = false, connected = false, screenKey = '', closedFor = null } = {}) {
  if (publishing || asked) return true;
  if (closedFor != null && closedFor === screenKey) return false;
  return !!(connected || screenKey);
}

// ── the check-in's "Something lighter" ──────────────────────────────────────
/**
 * The interjection body for care_lighter (a piggyback, no message). A seat
 * proves itself with its secret, like every other piggyback; a viewer with no
 * seat sends none (the backend accepts a spectator). Nothing else: the request
 * carries no feeling, no text, nothing about the viewer.
 */
export function careLighterBody({ gameId, playerId = null, playerSecret = null } = {}) {
  const body = { game_id: gameId, care_lighter: true };
  if (playerId) {
    body.player_id = playerId;
    body.player_secret = playerSecret || null;
  }
  return body;
}

/**
 * POST it on the interjection route. → the server's state ('queued' ·
 * 'cooldown' · 'off') or 'error'. Never throws: the card already said it
 * asked, and nothing on the page waits on the answer.
 */
export async function sendCareLighter({ baseUrl, gameId, playerId = null, playerSecret = null, fetchImpl = null } = {}) {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch : null);
  if (!doFetch || !baseUrl || !gameId) return 'error';
  try {
    const res = await doFetch(`${baseUrl}/chat-game/interjection`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(careLighterBody({ gameId, playerId, playerSecret })),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return 'error';
    return typeof data.care_lighter === 'string' ? data.care_lighter : 'off';
  } catch (e) {
    return 'error';
  }
}
